/*
 * Resolved runtime config.
 *
 * VITE_DEPLOY_TARGET (cloud | edge) is the ONE deploy-shape selector we keep — it
 * mirrors the backend mode and genuinely can't be derived (a cloud build and an
 * edge build differ in BEHAVIOUR, not just hostname). Everything else is a baked
 * constant or DERIVED at runtime from the document origin (no-env-vars rule):
 *
 *   cloud — app.vitesseautomation.ai (Cloudflare Pages) → cloud api-gateway
 *   edge  — <fp>.gateway.vitesseautomation.ai (served by the gateway nginx) →
 *           same-origin api-gateway; offline device-signed LST fallback active.
 *           Per-NIC LAN race legs are it/ot/wifi.<fp>.gateway.vitesseautomation.ai
 *           (Cloudflare A-records → real LAN IPs; the edge wildcard cert covers them).
 */
export type DeployTarget = 'cloud' | 'edge';

export const DEPLOY_TARGET: DeployTarget = import.meta.env?.VITE_DEPLOY_TARGET === 'edge' ? 'edge' : 'cloud';

export const IS_EDGE = DEPLOY_TARGET === 'edge';

/**
 * Clerk publishable key — PUBLIC (ships in every bundle), one production instance shared by every
 * app, so a session and its `pla`/`fea` claims are the same everywhere. A baked CONSTANT.
 *
 * THE KEY ENCODES THE HOST: `pk_live_<base64("clerk.<domain>$")>`. Two consequences worth keeping
 * in mind together, because the first invites the mistake the second punishes — a domain move
 * cannot be done by find-and-replace, and a hand-built key for a domain whose instance does not
 * exist yet is WELL-FORMED AND DEAD: every app fails to authenticate with nothing in the diff that
 * looks wrong. Copy the key Clerk gives you; never construct it.
 */
export const CLERK_PUBLISHABLE_KEY = 'pk_live_Y2xlcmsudml0ZXNzZWF1dG9tYXRpb24uYWkk';

/*
 * The origin this code is running under — and it must never come back empty.
 *
 * Every base URL below falls back to this, and Connect treats an empty baseUrl as
 * a RELATIVE one: the RPC path is then resolved against whatever route the page
 * happens to be on. That failure is invisible from the home page — `/` + a
 * relative path is the right URL by accident — and 404s everywhere else, so the
 * same call works or does not depending on which screen you opened the dialog
 * from. Both `origin` sources are checked because a Worker created from a
 * `blob:` URL reports a usable `origin` but an EMPTY `hostname`, and the sibling
 * lookup keys off the hostname.
 */
const sameOrigin = (): string => {
  const origin = globalThis.location?.origin;
  if (origin && origin !== 'null') return origin;
  // A blob-origin worker: fall back to the document that spawned it. Better a
  // slightly indirect answer than a relative URL nobody notices is relative.
  const base = (globalThis as { origin?: string }).origin;
  return base && base !== 'null' ? base : '';
};
// Sibling planes are DERIVED from whichever apex served this page — see @vitesse/base-ui/zones.
// MUST be the subpath, never the barrel: this module is reachable from the Backend Worker.
import { siblingUrl } from '@vitesse/base-ui/zones';

/*
 * api-gateway base URL — DERIVED from the origin, never configured:
 *   edge                              → same-origin (gateway nginx reverse-proxies
 *                                       Connect to the local api-gateway)
 *   cloud @ app.<apex>               → the cloud api-gateway: the ONE public
 *                                       front door (it also reverse-proxies
 *                                       FleetControl — one host, one cert)
 *   cloud @ anywhere else             → same-origin (a cloud build on the local
 *                                       dev server, whose vite proxy forwards
 *                                       Connect to the backend)
 */
/**
 * An RPC base URL that CANNOT be relative.
 *
 * Connect treats an empty or relative `baseUrl` as relative, and resolves the RPC
 * path against the current ROUTE. That is invisible from `/` — where the resolved
 * path is right by accident — and 404s on every deeper route, so the same call
 * succeeds or fails depending on which screen the user opened the dialog from.
 * It is the worst shape of bug: correct in the place you test it.
 *
 * So the value is resolved to an absolute URL here, once, and anything that
 * cannot be resolved throws at module load rather than becoming a relative URL
 * that works on one screen. A base URL is not a thing to be lenient about.
 */
function rpcBase(url: string, name: string): string {
  const origin = sameOrigin();
  if (!url && !origin) throw new Error(`${name}: no absolute base URL and no origin to derive one from`);
  const resolved = new URL(url || '/', origin || undefined).toString().replace(/\/$/, '');
  if (!/^https?:\/\//.test(resolved)) throw new Error(`${name}: base URL is not absolute (${resolved})`);
  return resolved;
}

export const API_GATEWAY_URL: string = rpcBase(IS_EDGE ? sameOrigin() : siblingUrl('cloud') || sameOrigin(), 'API_GATEWAY_URL');

/*
 * The ORG AUDIT LOG — a Cloudflare Worker, not the api-gateway, and one absolute
 * host rather than a per-deployment one.
 *
 * It is the same store from every surface ON PURPOSE. An edge box reads the org's
 * history from the same place the cloud app does, because that history belongs to
 * the ORG and not to any gateway: an org can exist with no hardware at all, and a
 * box changing hands must not take a company's records with it.
 *
 * A platform constant, never configuration — see the env-var rule.
 */
export const AUDIT_URL = 'https://audit.vitesseautomation.ai';

/*
 * LAN-direct candidates — the edge's OWN api-gateway, reached on the customer LAN
 * via the FIXED per-NIC Plex names `<role>.<fp>.gateway.vitesseautomation.ai`
 * (Cloudflare A-records → the box's real LAN IP; the `*.<fp>...` wildcard cert
 * covers them → trusted TLS to a LAN IP, no warning). The URL contract is now the
 * ONE shared copy in @vitesse/base-ui so it CANNOT drift between this app and the
 * fleet admin; re-exported here so `./env` stays the app's single env surface.
 * Adopting the legs (the LAN-vs-cloud race + failover) is the Backend Worker's
 * concern; cloud stays the default + guaranteed path, a LAN leg is a latency upgrade.
 */
// MUST import from the '/plex' SUBPATH, never the '@vitesse/base-ui' barrel: this
// module is reachable from the Backend WORKER (backend.worker.ts imports API_GATEWAY_URL
// from here), and the barrel pulls in every Lit element — which touch `document` at
// module-eval and throw `document is not defined` in a Worker, blanking the whole app.
// plex.ts is pure (URL strings only, no DOM), so the subpath is Worker-safe.
export { EDGE_PLEX_DOMAIN, lanCandidates, type LanRole, type LanLeg } from '@vitesse/base-ui/plex';

/*
 * Cloud control-plane api-gateway base URL — for the FleetControl service
 * (device enrollment / org claim / fleet registry). FleetControl ALWAYS targets
 * the CLOUD api-gateway, never an edge: it's the one cross-org, cloud-scoped
 * hop, and a brand-new edge isn't yet on the NATS leaf (so claim can't ride it).
 *
 * DERIVED from the origin, never configured (no-env-vars), exactly like
 * API_GATEWAY_URL — and now the SAME host (the one front door also proxies
 * FleetControl):
 *   cloud @ app.<apex> → the prod cloud front door
 *   anywhere else (local dev server, edge build) → same-origin, so the dev
 *     server's vite proxy forwards `/vitesse.v1.FleetControl/*` to the
 *     cloud api-gateway (avoids browser CORS / mixed-content; the proxy
 *     terminates TLS to the cloud host). The Connect path is the routing key.
 */
export const CLOUD_API_GATEWAY_URL: string = rpcBase(siblingUrl('cloud') || sameOrigin(), 'CLOUD_API_GATEWAY_URL');

/*
 * AI plane base URL — the ai-worker (handshake + per-user conversation
 * append/subscribe). A SEPARATE origin from the api-gateway: the AI plane is
 * Cloudflare (off the Hetzner core), so `ai.` not `cloud.`. DERIVED from the
 * origin, never configured (no-env-vars), same shape as CLOUD_API_GATEWAY_URL:
 *   prod @ app.<apex> → the prod AI worker (cross-origin; the AI
 *     worker must send CORS for app./fleet. — a follow-up on that side)
 *   anywhere else (local dev server, edge build) → same-origin, so the dev
 *     server's vite proxy forwards /handshake + /conv/* to the AI worker
 *     (avoids browser CORS; a proxy rule is added when the AI dev origin is wired)
 * Worker-safe: this module is imported from the AI web worker; self.location
 * resolves to the app origin, so the derivation matches the main thread.
 */
/*
 * SAME-ORIGIN, ALWAYS. This used to resolve to the sibling ai.<apex> in prod, which
 * made prod the only environment where the AI plane was cross-origin — a shape dev
 * could not reproduce, so each hole in the worker's CORS config shipped as a
 * prod-only failure. public/_worker.js now forwards the AI-plane prefixes to the
 * worker from this origin, exactly as the Vite proxy does in dev, so there is no
 * cross-origin request left to preflight.
 */
export const AI_URL: string = sameOrigin();

/*
 * Offline device-signed LST fallback only when the gateway itself serves the UI
 * (edge); cloud is online by definition.
 */
export const OFFLINE_LST_FALLBACK = IS_EDGE;
